import { SubscriptionTier } from '../config/subscriptionTiers';
import { filterProfileByTier } from './subscription';

export interface VCardContact {
  name: string;
  phone: string;
  email?: string;
  title?: string;
  organization?: string;
  department?: string;
  role?: string;
  homePhone?: string;
  workPhone?: string;
  mobilePhone?: string;
  faxPhone?: string;
  website?: string;
  blog?: string;
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  instagram?: string;
  github?: string;
  youtube?: string;
  tiktok?: string;
  skype?: string;
  telegram?: string;
  whatsapp?: string;
  nickname?: string;
  gender?: string;
  birthdate?: string;
  anniversary?: string;
  address?: any[];
  notes?: string;
  picture?: string;
  flickrGalleryUrl?: string;
  dropboxShareUrl?: string;
  googleDriveUrl?: string;
  categories?: string[];
  interests?: string;
  expertise?: string;
}

export const generateVCardData = (profile: any, tier: SubscriptionTier = 'FREE'): string => {
  // Filter profile data based on subscription tier
  const filteredProfile = filterProfileByTier(profile, tier);

  const vCardLines = [
    'BEGIN:VCARD',
    'VERSION:4.0',
    `FN:${filteredProfile.name || ''}`,
  ];

  // Name components
  const nameParts = (filteredProfile.name || '').split(' ');
  const lastName = nameParts.pop() || '';
  const firstName = nameParts.join(' ');
  vCardLines.push(`N:${lastName};${firstName};;;`);

  // Basic contact info
  if (filteredProfile.email) vCardLines.push(`EMAIL:${filteredProfile.email}`);
  if (filteredProfile.phone) vCardLines.push(`TEL;TYPE=CELL:${filteredProfile.phone}`);
  if (filteredProfile.organization) vCardLines.push(`ORG:${filteredProfile.organization}`);
  if (filteredProfile.jobTitle) vCardLines.push(`TITLE:${filteredProfile.jobTitle}`);
  if (filteredProfile.department) vCardLines.push(`ORG-UNIT:${filteredProfile.department}`);
  if (filteredProfile.role) vCardLines.push(`ROLE:${filteredProfile.role}`);
  if (filteredProfile.website) vCardLines.push(`URL:${filteredProfile.website}`);

  // Multiple phones if they exist
  if (filteredProfile.homePhone) vCardLines.push(`TEL;TYPE=HOME:${filteredProfile.homePhone}`);
  if (filteredProfile.workPhone) vCardLines.push(`TEL;TYPE=WORK:${filteredProfile.workPhone}`);
  if (filteredProfile.mobilePhone) vCardLines.push(`TEL;TYPE=CELL:${filteredProfile.mobilePhone}`);
  if (filteredProfile.faxPhone) vCardLines.push(`TEL;TYPE=FAX:${filteredProfile.faxPhone}`);

  // Address handling
  if (filteredProfile.address && filteredProfile.address.length > 0) {
    filteredProfile.address.forEach((addr: any) => {
      if (addr) {
        const components = [
          '',  // PO Box
          '',  // Extended
          addr.street || '',
          addr.city || '',
          addr.state || '',
          addr.postalCode || '',
          addr.country || ''
        ];
        vCardLines.push(`ADR:${components.join(';')}`);
      }
    });
  }

  // Social media profiles using X-SOCIALPROFILE
  if (filteredProfile.linkedin) vCardLines.push(`X-SOCIALPROFILE;TYPE=linkedin:${filteredProfile.linkedin}`);
  if (filteredProfile.twitter) vCardLines.push(`X-SOCIALPROFILE;TYPE=twitter:${filteredProfile.twitter}`);
  if (filteredProfile.facebook) vCardLines.push(`X-SOCIALPROFILE;TYPE=facebook:${filteredProfile.facebook}`);
  if (filteredProfile.instagram) vCardLines.push(`X-SOCIALPROFILE;TYPE=instagram:${filteredProfile.instagram}`);
  if (filteredProfile.youtube) vCardLines.push(`X-SOCIALPROFILE;TYPE=youtube:${filteredProfile.youtube}`);
  if (filteredProfile.tiktok) vCardLines.push(`X-SOCIALPROFILE;TYPE=tiktok:${filteredProfile.tiktok}`);
  if (filteredProfile.github) vCardLines.push(`X-SOCIALPROFILE;TYPE=github:${filteredProfile.github}`);
  if (filteredProfile.skype) vCardLines.push(`X-SOCIALPROFILE;TYPE=skype:${filteredProfile.skype}`);
  if (filteredProfile.telegram) vCardLines.push(`X-SOCIALPROFILE;TYPE=telegram:${filteredProfile.telegram}`);
  if (filteredProfile.whatsapp) vCardLines.push(`X-SOCIALPROFILE;TYPE=whatsapp:${filteredProfile.whatsapp}`);

  // Add custom X-properties for file sharing
  if (filteredProfile.flickrGalleryUrl) {
    vCardLines.push(`X-FLICKR-GALLERY:${filteredProfile.flickrGalleryUrl}`);
  }
  if (filteredProfile.dropboxShareUrl) {
    vCardLines.push(`X-DROPBOX-SHARE:${filteredProfile.dropboxShareUrl}`);
  }
  if (filteredProfile.googleDriveUrl) {
    vCardLines.push(`X-GDRIVE-SHARE:${filteredProfile.googleDriveUrl}`);
  }

  // Additional fields
  if (filteredProfile.notes) vCardLines.push(`NOTE:${filteredProfile.notes}`);
  if (filteredProfile.picture) vCardLines.push(`PHOTO:${filteredProfile.picture}`);
  if (filteredProfile.nickname) vCardLines.push(`NICKNAME:${filteredProfile.nickname}`);
  if (filteredProfile.birthdate) vCardLines.push(`BDAY:${new Date(filteredProfile.birthdate).toISOString().split('T')[0]}`);
  if (filteredProfile.categories?.length > 0) vCardLines.push(`CATEGORIES:${filteredProfile.categories.join(',')}`);
  if (filteredProfile.interests) vCardLines.push(`X-INTERESTS:${filteredProfile.interests}`);
  if (filteredProfile.expertise) vCardLines.push(`X-EXPERTISE:${filteredProfile.expertise}`);

  // Country as a separate field if not part of address
  if (filteredProfile.country) vCardLines.push(`X-COUNTRY:${filteredProfile.country}`);

  vCardLines.push('END:VCARD');
  return vCardLines.join('\n');
};

export const downloadVCard = (profile: any, tier: SubscriptionTier = 'FREE') => {
  const vCardData = generateVCardData(profile, tier);
  const blob = new Blob([vCardData], { type: 'text/vcard;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  
  // Open the vCard in a new window/tab
  window.open(url, '_blank');
  
  // Clean up the URL object after a short delay to ensure it's loaded
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 100);
};